import { array, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { CheckInConfigurationType, Formula, FormulaType } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { localizeText as globalizationLocalizeText } from '@mews-commander/utils';

const variableDictionary: Record<string, Mews.TextKey> = {
    Local: TextKey.FormulaLocal,
    Schengen: TextKey.FormulaSchengen,
    SchengenVisaRequired: TextKey.FormulaSchengenVisaRequired,
    Eu: TextKey.FormulaEu,
    HongKong: TextKey.FormulaHongKong,
    Nordic: TextKey.FormulaNordic,
    IdentityCardExpirationDateNotRequired: TextKey.FormulaIdentityCardExpirationDateNotRequired,
    DriversLicenseExpirationDateNotRequired: TextKey.FormulaDriversLicenseExpirationDateNotRequired,
    PassportExpirationDateNotRequired: TextKey.FormulaPassportExpirationDateNotRequired,
    VisaExpirationDateNotRequired: TextKey.FormulaVisaExpirationDateNotRequired,
};

interface CombineRequirementsOptions {
    localize: (textKey: Mews.TextKey, text: string) => string;
}

const combineRequirements = (formula: Formula, { localize }: CombineRequirementsOptions): string => {
    const { discriminator, value } = formula.value;

    switch (discriminator) {
        // This will never happen irl..
        case FormulaType.Constant: {
            const formulaValue = value.value;
            if (formulaValue) {
                return localize(TextKey.FormulaAlways, 'Always');
            }
            return localize(TextKey.FormulaNever, 'Never');
        }
        case FormulaType.Negation: {
            const { formula: nestedFormula } = value;
            return `${localize(TextKey.FormulaNot, 'NOT')} ${combineRequirements(nestedFormula, { localize })}`;
        }
        case FormulaType.Variable: {
            const variableValue = value.value;
            const variableTextKey = variableDictionary[variableValue];
            return variableTextKey != null ? localize(variableTextKey, variableValue) : variableValue;
        }
        case FormulaType.Conjunction: {
            return pipe(
                value.formulas,
                array.map((innerFormula) => combineRequirements(innerFormula, { localize })),
                (res) => res.join(` ${localize(TextKey.FormulaAnd, 'AND')} `),
                (res) => `(${res})`
            );
        }
        case FormulaType.Disjunction: {
            return pipe(
                value.formulas,
                array.map((innerFormula) => combineRequirements(innerFormula, { localize })),
                (res) => res.join(` ${localize(TextKey.FormulaOr, 'OR')} `),
                (res) => `(${res})`
            );
        }
    }
};

interface ProcessFormulaOptions {
    localizeText?: (text: Mews.TextKey) => string;
}

export const processFormula = (formula: Formula, { localizeText }: ProcessFormulaOptions = {}): string => {
    const { discriminator, value } = formula.value;

    const localize = (textKey: Mews.TextKey, text: string) => (localizeText != null ? localizeText(textKey) : text);

    switch (discriminator) {
        case FormulaType.Constant: {
            const formulaValue = value.value;
            if (formulaValue) {
                return localize(TextKey.FormulaAlways, 'Always');
            }
            return localize(TextKey.FormulaNever, 'Never');
        }
        case FormulaType.Negation: {
            const { formula: nestedFormula } = value;
            return `${localize(TextKey.FormulaYesUnless, 'Yes unless')} ${combineRequirements(nestedFormula, {
                localize,
            })}`;
        }
        case FormulaType.Variable: {
            return `${localize(TextKey.FormulaYesIf, 'Yes if')} ${value.value}`;
        }
        case FormulaType.Conjunction: {
            return `${localize(TextKey.FormulaYesIf, 'Yes if')} ${combineRequirements(formula, { localize })}`;
        }
        case FormulaType.Disjunction: {
            return `${localize(TextKey.FormulaYesIf, 'Yes if')} ${combineRequirements(formula, { localize })}`;
        }
    }
};

const messageDictionary: Record<string, Mews.TextKey> = {
    Always: TextKey.FieldIsRequired,
    Never: TextKey.FieldIsNotRequired,
    'Yes unless PassportExpirationDateNotRequired': TextKey.FieldIsRequiredUnlessExpirationDate,
    'Yes unless VisaExpirationDateNotRequired': TextKey.FieldIsRequiredUnlessExpirationDate,
    'Yes unless DriversLicenseExpirationDateNotRequired': TextKey.FieldIsRequiredUnlessExpirationDate,
    'Yes unless IdentityCardExpirationDateNotRequired': TextKey.FieldIsRequiredUnlessExpirationDate,
    'Yes if Local': TextKey.FieldIsRequiredIfSameGuestCountry,
    'Yes unless Local': TextKey.FieldIsRequiredUnlessSameGuestCountry,
    'Yes unless (Eu OR Schengen)': TextKey.FieldIsRequiredUnlessGuestNationalityEuOrSchengen,
};

interface GetConfigurationTooltipOptions extends ProcessFormulaOptions {
    type: CheckInConfigurationType;
    formula?: Formula;
}

/**
 *
 * Pass this type `ConfigurationTooltip` into `getTooltipLabel` to get a translation or `null` if the translation could not be created.
 *
 * - `empty` represents no translation
 * - `notTranslated` means we didn't find manually matched translation so we give you fallback that reads more like code than natural language. For example, "Yes if Local".
 * - `translated` means we found manually matched translation, but we need you to provide `fieldName` to translate the `TextKey`.
 *
 * We use this unwieldy approach because `fieldName` is not accessible until we render the individual fields, which cannnot evaluate formula.
 *
 * It's because `formula` has a recursive type, and the TypeScript in our Table could not handle it as written currently. Also, evaluating formula at the top also lets us evaluate the formula for each field only once.
 *
 * I tried to refactor the code to remove the need for `ConfigurationTooltip`. However, since we have overrides for field names in `DocumentSectionTables.tsx`, I couldn't account for them at the top where we create the tooltip values.
 *
 */

export type ConfigurationTooltip =
    | { type: 'empty' }
    | { type: 'notTranslated'; value: string }
    | { type: 'translated'; textKey: Mews.TextKey };

export const getConfigurationTooltip = ({
    formula,
    localizeText,
    type,
}: GetConfigurationTooltipOptions): ConfigurationTooltip => {
    if (type === CheckInConfigurationType.VerificationPhoto) {
        return {
            type: 'translated',
            textKey: TextKey.VerificationPhotoConfigTooltip,
        };
    }

    if (formula == null) {
        if (type === CheckInConfigurationType.Phone || type === CheckInConfigurationType.PurposeOfStay) {
            return {
                type: 'translated',
                textKey: TextKey.ConfigurationFieldOptionOptional,
            };
        }
        return { type: 'empty' };
    }

    const messageString = processFormula(formula);

    return pipe(
        messageDictionary,
        record.lookup(messageString),
        option.foldW(
            () => ({
                type: 'notTranslated',
                value: processFormula(formula, { localizeText }),
            }),
            (textKey) => ({
                type: 'translated',
                textKey,
            })
        )
    );
};

interface TooltipLabelProps {
    tooltipValue: ConfigurationTooltip;
    fieldName: string;
}

/**
 * Translates `ConfigurationTooltip` into a human-readable string.
 *
 * @returns The translated formula, or `null` if the translation could not be created.
 */
export const getTooltipLabel = ({ fieldName, tooltipValue }: TooltipLabelProps) => {
    switch (tooltipValue.type) {
        case 'empty':
            return null;
        case 'notTranslated':
            return tooltipValue.value;
        case 'translated':
            return globalizationLocalizeText(tooltipValue.textKey, { fieldName });
    }
};
